import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.auth.validateInvite(token!),
    enabled: !!token,
  });

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || password.length < 8) {
      toast.error('Name and password (min 8 chars) required');
      return;
    }
    setLoading(true);
    try {
      const result = await api.auth.acceptInvite(token!, { name, password });
      setUser(result.user);
      toast.success('Welcome to NOETED!');
      navigate('/');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal border-t-transparent" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-4 text-center">
          <h1 className="text-2xl font-bold text-teal">NOETED</h1>
          <p className="text-sm text-compliance-critical">This invitation link is invalid or has expired.</p>
          <Link to="/login" className="text-sm text-teal hover:underline">Go to login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-teal">NOETED</h1>
          <h2 className="mt-2 text-lg font-semibold">Join {data.orgName as string}</h2>
          <p className="mt-1 text-sm text-muted-foreground">You've been invited as <span className="font-medium text-foreground">{(data.role as string).replace('_', ' ')}</span></p>
        </div>

        <form onSubmit={handleAccept} className="space-y-4 rounded-lg border border-border bg-card p-6">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Email</label>
            <Input value={data.email as string} disabled className="mt-1 bg-muted" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Your Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Password</label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 characters" className="mt-1" />
          </div>
          <Button type="submit" className="w-full bg-teal text-white hover:bg-teal/90" disabled={loading}>
            {loading ? 'Setting up...' : 'Accept Invitation'}
          </Button>
        </form>
      </div>
    </div>
  );
}
